import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Globe, Server, Shield, Zap, Code, Cpu, Cloud, Rocket, Layers, Monitor } from 'lucide-react';
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";

const WebDesign = () => {
  const technologies = [
    { icon: <Code />, name: "React / Next.js", desc: "Modern frontend frameworks" },
    { icon: <Server />, name: "Cloud Infrastructure", desc: "AWS, Azure, GCP" },
    { icon: <Cpu />, name: "AI-Assisted Dev", desc: "Faster, smarter builds" },
    { icon: <Shield />, name: "Enterprise Security", desc: "SOC2 compliant hosting" },
    { icon: <Zap />, name: "Edge CDN", desc: "Global content delivery" },
    { icon: <Cloud />, name: "Auto-Scaling", desc: "Handle any traffic load" }
  ];

  const features = [
    {
      title: "AI-Powered Design",
      description: "Our advanced AI tools analyze your brand, industry, and target audience to create stunning designs that convert. We leverage machine learning to optimize layouts, colors, and user journeys.",
      highlights: ["Intelligent layout optimization", "A/B testing automation", "Conversion-focused design", "Brand consistency AI"]
    },
    {
      title: "Enterprise Cloud Hosting",
      description: "Your website runs on the same infrastructure powering Fortune 500 companies. Multi-region deployment, automatic failover, and 99.99% uptime guaranteed.",
      highlights: ["99.99% uptime SLA", "Multi-region redundancy", "Automatic SSL certificates", "DDoS protection included"]
    },
    {
      title: "Performance Optimization",
      description: "Every millisecond matters. Our sites achieve perfect Lighthouse scores with advanced caching, image optimization, and code splitting.",
      highlights: ["Sub-second load times", "Perfect Core Web Vitals", "Image optimization AI", "Lazy loading & prefetching"]
    }
  ];

  const portfolioExamples = [
    {
      title: "Luxury Resort Platform",
      category: "Hospitality",
      image: "https://images.pexels.com/photos/258154/pexels-photo-258154.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600",
      stats: ["300% conversion increase", "1.2s load time", "50K+ monthly visitors"],
      tech: ["React", "Node.js", "AWS"]
    },
    {
      title: "Real Estate Portal",
      category: "Real Estate",
      image: "https://images.pexels.com/photos/323780/pexels-photo-323780.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600",
      stats: ["500+ property listings", "AI-powered search", "Mobile-first design"],
      tech: ["Next.js", "GraphQL", "Vercel"]
    },
    {
      title: "Travel Booking Engine",
      category: "Travel & Tourism",
      image: "https://images.pexels.com/photos/2007401/pexels-photo-2007401.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600",
      stats: ["$2M+ bookings processed", "Real-time availability", "Multi-language support"],
      tech: ["Vue.js", "Python", "Azure"]
    },
    {
      title: "Corporate SaaS Dashboard",
      category: "Technology",
      image: "https://images.pexels.com/photos/669615/pexels-photo-669615.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=400&w=600",
      stats: ["10K+ active users", "Real-time analytics", "Custom integrations"],
      tech: ["React", "FastAPI", "GCP"]
    }
  ];

  const hostingPlans = [
    {
      name: "Starter",
      price: "$99",
      period: "/month",
      features: ["5 pages", "SSL certificate", "Basic CDN", "Email support", "Monthly backups"],
      cta: "Get Started"
    },
    {
      name: "Professional",
      price: "$299",
      period: "/month",
      popular: true,
      features: ["Unlimited pages", "Advanced CDN", "Priority support", "Daily backups", "Analytics dashboard", "A/B testing"],
      cta: "Most Popular"
    },
    {
      name: "Enterprise",
      price: "Custom",
      period: "",
      features: ["Multi-site management", "Dedicated infrastructure", "24/7 phone support", "Custom SLA", "White-label option", "API access"],
      cta: "Contact Sales"
    }
  ];

  return (
    <div data-testid="web-design-page">
      {/* Hero Section */}
      <section className="relative py-20 lg:py-28 bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-72 h-72 bg-[#EA580C] rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#0D9488] rounded-full blur-3xl" />
        </div>
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-block px-4 py-1.5 bg-white/10 text-white text-xs font-bold uppercase tracking-wider rounded-full mb-6">
              World-Class Web Solutions
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter text-white mb-6">
              State-of-the-Art{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#EA580C] to-[#0D9488]">
                Web Design & Hosting
              </span>
            </h1>
            <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
              We build blazing-fast, AI-powered websites on enterprise-grade infrastructure. 
              Your digital presence deserves cutting-edge technology.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/partner">
                <Button 
                  size="lg" 
                  className="bg-[#EA580C] hover:bg-[#c2410c] text-white px-8 py-6 text-base"
                  data-testid="webdesign-cta-partner"
                >
                  Start Your Project
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/portfolio">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base"
                  data-testid="webdesign-cta-portfolio"
                >
                  View Our Work
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Technology Stack */}
      <section className="py-12 bg-[#0F172A] border-t border-slate-800">
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <p className="text-slate-400 text-sm uppercase tracking-wider">Powered by Industry-Leading Technology</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {technologies.map((tech, idx) => (
              <div key={idx} className="text-center group">
                <div className="w-12 h-12 mx-auto mb-3 bg-slate-800 rounded-xl flex items-center justify-center text-[#0D9488] group-hover:bg-[#0D9488] group-hover:text-white transition-all">
                  {tech.icon}
                </div>
                <h4 className="text-white font-semibold text-sm">{tech.name}</h4>
                <p className="text-slate-500 text-xs">{tech.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="section-padding bg-white" data-testid="webdesign-features">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
              Advanced Technology
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Built for the Modern Web
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We don't just build websites—we engineer digital experiences using the most 
              advanced tools and methodologies available.
            </p>
          </div>

          <div className="space-y-12">
            {features.map((feature, idx) => (
              <div key={idx} className={`grid lg:grid-cols-2 gap-12 items-center ${idx % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className={idx % 2 === 1 ? 'lg:order-2' : ''}>
                  <h3 className="text-2xl font-bold text-slate-900 mb-4">{feature.title}</h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">{feature.description}</p>
                  <ul className="grid sm:grid-cols-2 gap-3">
                    {feature.highlights.map((highlight, hidx) => (
                      <li key={hidx} className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-[#0D9488]" />
                        <span className="text-slate-700 text-sm">{highlight}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className={`bg-gradient-to-br from-slate-100 to-slate-200 rounded-2xl p-8 lg:p-12 ${idx % 2 === 1 ? 'lg:order-1' : ''}`}>
                  <div className="grid grid-cols-2 gap-4">
                    {[
                      { icon: <Monitor />, label: "Responsive" },
                      { icon: <Rocket />, label: "Fast" },
                      { icon: <Shield />, label: "Secure" },
                      { icon: <Layers />, label: "Scalable" }
                    ].map((item, iidx) => (
                      <div key={iidx} className="bg-white rounded-xl p-4 text-center shadow-sm">
                        <div className="w-10 h-10 mx-auto mb-2 bg-[#1E3A8A] rounded-lg flex items-center justify-center text-white">
                          {item.icon}
                        </div>
                        <span className="text-sm font-medium text-slate-700">{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Showcase */}
      <section className="section-padding bg-slate-50" data-testid="webdesign-portfolio">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
              Our Portfolio
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              World-Class Web Experiences
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From luxury brands to high-traffic platforms, we've built digital solutions 
              that drive real business results.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {portfolioExamples.map((project, idx) => (
              <Card key={idx} className="overflow-hidden border-slate-200 card-hover group" data-testid={`portfolio-item-${idx}`}>
                <div className="relative h-56 overflow-hidden">
                  <img 
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <span className="inline-block px-3 py-1 bg-[#EA580C] text-white text-xs font-bold uppercase rounded-full mb-2">
                      {project.category}
                    </span>
                    <h3 className="text-xl font-bold text-white">{project.title}</h3>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="flex flex-wrap gap-2 mb-4">
                    {project.tech.map((t, tidx) => (
                      <span key={tidx} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded">
                        {t}
                      </span>
                    ))}
                  </div>
                  <ul className="space-y-2">
                    {project.stats.map((stat, sidx) => (
                      <li key={sidx} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle className="w-4 h-4 text-[#0D9488]" />
                        {stat}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Hosting Plans */}
      <section className="section-padding bg-white" data-testid="hosting-plans">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500 mb-4 block">
              Hosting Plans
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">
              Enterprise-Grade Hosting
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Your website deserves infrastructure that never sleeps. Choose a plan that 
              scales with your business.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {hostingPlans.map((plan, idx) => (
              <Card 
                key={idx} 
                className={`relative border-2 ${plan.popular ? 'border-[#1E3A8A] shadow-xl' : 'border-slate-200'}`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 bg-[#1E3A8A] text-white text-xs font-bold uppercase rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{plan.name}</h3>
                  <div className="mb-6">
                    <span className="text-4xl font-black text-slate-900">{plan.price}</span>
                    <span className="text-slate-500">{plan.period}</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, fidx) => (
                      <li key={fidx} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle className="w-4 h-4 text-[#0D9488]" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Link to="/partner">
                    <Button 
                      className={`w-full ${plan.popular ? 'bg-[#1E3A8A] hover:bg-[#1e40af]' : 'bg-slate-900 hover:bg-slate-800'}`}
                    >
                      {plan.cta}
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-gradient py-20">
        <div className="container-max mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-black text-white mb-6">
            Ready for a World-Class Website?
          </h2>
          <p className="text-slate-300 mb-10 max-w-2xl mx-auto">
            Let's discuss how our advanced technology can transform your digital presence 
            and drive real business results.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/partner">
              <Button 
                size="lg" 
                className="bg-[#EA580C] hover:bg-[#c2410c] text-white px-8"
                data-testid="webdesign-final-cta"
              >
                Start Your Project
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline"
                className="border-white/30 text-white hover:bg-white/10 px-8"
              >
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebDesign;
